import { css } from '@emotion/core';
import { useContext, useEffect, useState } from 'react';
import { useUserSessionContext } from '../../../components/UserSessionContextWrapper';
import useMessageService from '../../../services/i18n/useMessageService';
import MyAccountLayout from '../../../components/MyAccountLayout/MyAccountLayout';
import { userEmailsInfo } from '../myAccountApi';
import { showErrorNotificationFromResponse } from '../../../services/errorUtils';
import ResponseStatus from '../../../constants/ResponseStatus';
import NavigationTabs from '../../../constants/NavigationTabs';
import { isMobile } from '../../../services/DeviceInformation';
import mobileContentWidthCSS from '../../../constants/mobileContentWidthCSS';
import AuthenticatedRouteLanguageContext from '../../../services/AuthenticatedRouteLanguageContext';
import useNetworkErrorNotification from '../../../services/useNetworkErrorNotification';
import EmailEdit from './EmailEdit';
import EmailView from './EmailView';
import EmailSuccess from './EmailSuccess';

const MODES = {
    VIEW: 'VIEW',
    EDIT: 'EDIT',
    SUCCESS: 'SUCCESS',
};

function Emails() {
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);
    const [emailsInfo, setEmailsInfo] = useState({});
    const [newEmail, setNewEmail] = useState();
    const { handleResponseSessionErrors } = useUserSessionContext();
    const [mode, setMode] = useState(MODES.VIEW);
    const { showNotification: showNetworkErrorNotification } = useNetworkErrorNotification();

    const [
        [
            cancelLabel = 'Cancel',
            editLabel = 'Edit',
            invalidEmailError = 'Invalid email format entered',
            verifyLabel = 'Verify',
            usernameTooltipLabel = 'Your primary email is your VeevaID and your username.',
            primaryEmailLabel = 'Primary Email (Your Username)',
            confirmationSentLabel = 'An email has been sent to:',
            confirmationSentInstructionLabel = 'Please follow the email instructions to verify and update your email.',
        ],
    ] = useMessageService([
        'general.cancel',
        'general.edit',
        'general.invalid_email',
        'general.verify',
        'my_account.username_tooltip',
        'my_account.primary_email_your_username',
        'my_account.email_change_confirmation_sent',
        'my_account.email_change_confirmation_sent_instruction',
    ]);

    const fetchInfo = () => {
        let isCancelled = false;
        userEmailsInfo().then(async (response) => {
            if (!isCancelled) {
                if (handleResponseSessionErrors(response)) {
                    return;
                }

                switch (response.responseStatus) {
                    case ResponseStatus.SUCCESS: {
                        setEmailsInfo(response.data);
                        break;
                    }
                    case ResponseStatus.FAILURE: {
                        const i18nOptions = { language: messageLanguage };
                        await showErrorNotificationFromResponse(response, i18nOptions);
                        break;
                    }
                    case ResponseStatus.UI_NETWORK_ERROR: {
                        await showNetworkErrorNotification();
                        break;
                    }
                    default:
                    // No-op
                }
            }
        });

        return () => {
            isCancelled = true;
        };
    };

    useEffect(() => {
        fetchInfo();
        // This hook automatically runs on mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showViewMode = () => {
        setMode(MODES.VIEW);
    };

    const showEditMode = () => {
        setMode(MODES.EDIT);
        setNewEmail(emailsInfo.primaryEmail);
    };

    const showSuccessMode = (updatedEmail) => {
        setMode(MODES.SUCCESS);
        setNewEmail(updatedEmail);
    };

    const labels = {
        cancelLabel,
        editLabel,
        verifyLabel,
        invalidEmailError,
        usernameTooltipLabel,
        primaryEmailLabel,
        confirmationSentLabel,
        confirmationSentInstructionLabel,
    };

    const content = (
        <>
            {mode === MODES.VIEW && (
                <EmailView email={emailsInfo.primaryEmail} labels={labels} onEdit={showEditMode} />
            )}
            {mode === MODES.EDIT && (
                <EmailEdit
                    currentEmail={emailsInfo.primaryEmail}
                    labels={labels}
                    onCancel={showViewMode}
                    onSuccess={showSuccessMode}
                />
            )}
            {mode === MODES.SUCCESS && <EmailSuccess newEmail={newEmail} labels={labels} />}
        </>
    );
    if (isMobile()) {
        return (
            <MyAccountLayout selectedTab={NavigationTabs.EMAILS}>
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        align-items: center;

                        & > div {
                            ${mobileContentWidthCSS};
                        }
                    `}
                >
                    {content}
                </div>
            </MyAccountLayout>
        );
    }

    return <MyAccountLayout selectedTab={NavigationTabs.EMAILS}>{content}</MyAccountLayout>;
}

export default Emails;
